import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns/format';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import cl from 'classnames';
import { AggregationDayType } from 'src/models';
import { PaymentsChart } from 'src/interface';
import { ALL_ITEM_ID, REPORTS } from 'src/constants';
import { compareString, isWeekend } from 'src/helper';
import { usePage, useLanguage, useContextData } from 'src/hooks';
import { useAppDispatch } from 'src/store';
import { useAccountState } from 'src/services/auth';
import {
  usePaymentsAnalyticsState,
  fetchCompaniesPaymentsFlowAction,
} from 'src/services/analytics';
import { PageSwitcher } from 'src/components';
import {
  FilterModal,
  FilterModalButton,
  useDateRangePicker,
  SelectWithLabel,
  GroupFilter,
  FilterModalIndicator,
} from 'src/components/general';
import styles from './serviceAnalyticsStyle.module.scss';

type Scale = 1 | 2 | 3 | 4;

export const ServiceAnalytics = () => {
  const {
    header: { width: headerWidth },
    screen: { isLaptop },
    layoutScroller: { scroll },
  } = useContextData();
  const {
    access: { accessMap },
  } = usePage();
  const dispatch = useAppDispatch();
  const { t, currentLang } = useLanguage();
  const { pending, entities } = usePaymentsAnalyticsState();
  const { user } = useAccountState();
  const [aggregation, setAggregation] = useState<AggregationDayType>(
    AggregationDayType.day,
  );
  const [aggregationModal, setAggregationModal] =
    useState<AggregationDayType>(aggregation);
  const [selectedCompany, setSelectedCompany] = useState<Set<string>>(
    new Set(),
  );
  const [modalSelectedCompany, setModalSelectedCompany] = useState<Set<string>>(
    new Set(),
  );
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [scale, setScale] = useState<Scale>(1);

  const filteredByCompanies = useMemo(
    () => Boolean(selectedCompany.size) && !selectedCompany.has(ALL_ITEM_ID),
    [selectedCompany],
  );

  useEffect(() => {
    scroll(0, 0);
  }, [scale, scroll]);

  const chartHeight = useMemo(() => (isLaptop ? 225 : 300), [isLaptop]);

  const selectAllCompanies = useMemo(
    () => ({
      id: ALL_ITEM_ID,
      label: t('general.allCompanies'),
    }),
    [t],
  );

  const filterItems = useMemo(
    () =>
      [
        {
          value: AggregationDayType.day,
          label: t('general.day'),
        },
        {
          value: AggregationDayType.week,
          label: t('general.week'),
        },
        {
          value: AggregationDayType.month,
          label: t('general.month'),
        },
      ] as { value: AggregationDayType; label: string }[],
    [t],
  );

  const companies: { id: string; label: string }[] = useMemo(() => {
    const entityCompanies = Object.keys(entities || {});

    if (!user?.companies || !entityCompanies.length) {
      return [];
    }

    return user.companies.reduce(
      (result, item) => {
        if (entityCompanies.includes(item.id)) {
          return [
            ...result,
            {
              id: item.id,
              label: item.name,
            },
          ];
        }

        return result;
      },
      [] as { id: string; label: string }[],
    );
  }, [user?.companies, entities]);

  const selectCompany = useCallback(
    (item: string, value: boolean, selected: Set<string>) => {
      if (item === ALL_ITEM_ID) {
        return selected.size - (selected.has(ALL_ITEM_ID) ? 1 : 0) <
          companies.length
          ? new Set([ALL_ITEM_ID, ...companies.map((item) => item.id)])
          : new Set<string>();
      }

      const updatedIDs = new Set(selected);
      updatedIDs.delete(ALL_ITEM_ID);

      if (value) {
        updatedIDs.add(item);
      } else {
        updatedIDs.delete(item);
      }

      if (updatedIDs.size === companies.length) {
        updatedIDs.add(ALL_ITEM_ID);
      }

      return updatedIDs;
    },
    [companies],
  );

  const paymentsChart: PaymentsChart[] = useMemo(() => {
    const paymentsMap = Object.entries(entities).reduce(
      (result, [companyId, { payments }]) => {
        if (selectedCompany.size > 0 && !selectedCompany.has(companyId)) {
          return result;
        }
        return payments.reduce((res, p) => {
          const item = res[p.date];
          const total =
            p.paymentSystemHeadsetSucceeded +
            p.paymentSystemQrCodeSucceeded +
            p.receptionSucceeded +
            p.vipAccessSucceeded +
            (item?.total || 0);

          if (!item) {
            return {
              ...res,
              [p.date]: {
                time: p.date,
                date: p.date,
                numberOfPayments: p.numberOfPayments,
                total,
                system: p.paymentSystemHeadsetSucceeded,
                qrCode: p.paymentSystemQrCodeSucceeded,
                reception: p.receptionSucceeded,
                vipAccess: p.vipAccessSucceeded,
                sessions: p.sessions,
              },
            };
          }

          return {
            ...res,
            [p.date]: {
              time: p.date,
              date: p.date,
              numberOfPayments: item.numberOfPayments + p.numberOfPayments,
              total,
              system: item.system + p.paymentSystemHeadsetSucceeded,
              qrCode: item.qrCode + p.paymentSystemQrCodeSucceeded,
              reception: item.reception + p.receptionSucceeded,
              vipAccess: item.vipAccess + p.vipAccessSucceeded,
              sessions: item.sessions + p.sessions,
            },
          };
        }, result);
      },
      {} as Record<string, PaymentsChart>,
    );

    return Object.values(paymentsMap)
      .sort((p1, p2) => compareString(p1.date, p2.date))
      .map((p) => ({ ...p, time: format(new Date(p.date), 'dd.MM') }));
  }, [entities, selectedCompany]);

  const paymentsCount = useMemo(
    () => paymentsChart.reduce((total, p) => total + p.numberOfPayments, 0),
    [paymentsChart],
  );

  const {
    range,
    dateRangePicker,
    modalDateRangePicker,
    applyDRPChanges,
    resetDRPChanges,
    clearDRP,
  } = useDateRangePicker({
    inputClassName: styles.datepickerInput,
    modalInputClassName: styles.datepickerModalInput,
    disabled: pending.companiesPayments,
  });

  const rangeIndicatorText = useMemo(
    () =>
      currentLang && range.start && range.end ? (
        <>
          <span className={styles.nowrap}>{range.start.localString}</span>
          {' - '}
          <span className={styles.nowrap}>{range.end.localString}</span>
        </>
      ) : undefined,
    [range.end, range.start, currentLang],
  );

  useEffect(() => {
    let start: string | undefined = undefined;
    let end: string | undefined = undefined;

    if (range.start && range.end) {
      start = range.start.format;
      end = range.end.format;
    }

    dispatch(
      fetchCompaniesPaymentsFlowAction({
        params: {
          start,
          end,
          aggregation: aggregation,
        },
      }),
    );
  }, [dispatch, range, aggregation]);

  return (
    <div className={styles.container}>
      <FilterModal
        show={showFiltersModal}
        close={() => {
          setShowFiltersModal(false);
        }}
        onApply={() => {
          applyDRPChanges();
          setSelectedCompany(modalSelectedCompany);
          setAggregation(aggregationModal);
          setShowFiltersModal(false);
        }}
        onCancel={() => {
          resetDRPChanges();
          setModalSelectedCompany(new Set());
          setAggregationModal(AggregationDayType.day);
        }}
      >
        {modalDateRangePicker}
        <GroupFilter
          label={t('general.selectedCompanies')}
          selectedIDs={modalSelectedCompany}
          items={[selectAllCompanies, ...companies]}
          selectItem={(item, value) => {
            setModalSelectedCompany(
              selectCompany(item, value, modalSelectedCompany),
            );
          }}
          size="small"
          className={styles.filterModal}
        />
        <SelectWithLabel
          label={t('general.period')}
          value={aggregationModal}
          items={filterItems}
          selectItem={(value) => {
            setAggregationModal(value as AggregationDayType);
          }}
          design="filterModal"
          size="small"
          className={styles.filterModal}
        />
      </FilterModal>
      <div className={styles.topSection}>
        <div
          className={cl(
            styles.pageSwitcherContainer,
            accessMap[REPORTS].length === 1 && styles.inactive,
          )}
        >
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
          <div className={styles.filterButtons}>
            <SelectWithLabel
              label={t('general.scale')}
              value={scale}
              items={
                [
                  { value: 1, label: '100%' },
                  { value: 2, label: '200%' },
                  { value: 3, label: '300%' },
                  { value: 4, label: '400%' },
                ] as { value: Scale; label: string }[]
              }
              selectItem={(value) => {
                setScale(value as Scale);
              }}
              design="filter"
              size="small"
              className={styles.scaleMobileFilter}
            />
            <FilterModalButton
              setShow={setShowFiltersModal}
              badge={filteredByCompanies || Boolean(rangeIndicatorText)}
              disabled={pending.companiesPayments}
            />
            <FilterModalIndicator
              icon="calendar"
              text={rangeIndicatorText}
              clear={clearDRP}
              disabled={pending.companiesPayments}
            />
          </div>
        </div>
        <div className={styles.filters}>
          {dateRangePicker}
          <GroupFilter
            label={t('general.selectedCompanies')}
            selectedIDs={selectedCompany}
            items={[selectAllCompanies, ...companies]}
            selectItem={(item, value) => {
              const updatedIDs = selectCompany(item, value, selectedCompany);

              setSelectedCompany(updatedIDs);
              setModalSelectedCompany(updatedIDs);
            }}
            className={styles.scaleFilter}
          />
          <SelectWithLabel
            label={t('general.period')}
            value={aggregation}
            items={filterItems}
            selectItem={(value) => {
              setAggregation(value as AggregationDayType);
              setAggregationModal(value as AggregationDayType);
            }}
            design="filter"
            className={styles.scaleFilter}
            disabled={pending.companiesPayments}
          />
          <SelectWithLabel
            label={t('general.scale')}
            value={scale}
            items={
              [
                { value: 1, label: '100%' },
                { value: 2, label: '200%' },
                { value: 3, label: '300%' },
                { value: 4, label: '400%' },
              ] as { value: Scale; label: string }[]
            }
            selectItem={(value) => {
              setScale(value as Scale);
            }}
            design="filter"
            className={styles.scaleFilter}
          />
        </div>
      </div>
      <div
        className={styles.contentSection}
        style={{ width: headerWidth * scale }}
      >
        <div className={styles.chartsSection}>
          <div className={styles.countBlock}>
            <div className={styles.countText} style={{ fontWeight: 600 }}>
              {t('page.reports.analytics.totalPayments')}
            </div>
            <div className={styles.countText}>{paymentsCount}</div>
          </div>
          <div className={styles.chart}>
            <ResponsiveContainer
              width={'100%'}
              height={chartHeight}
              className={cl(pending.companiesPayments && styles.opacity)}
            >
              <BarChart data={paymentsChart}>
                <Line type="monotone" dataKey="uv" stroke="#8884d8" />
                <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                {paymentsChart.length > 0 && <Legend />}
                <Bar
                  dataKey={'numberOfPayments'}
                  fill="#82ca9d"
                  name={t('page.reports.analytics.numberOfPayments')}
                >
                  {paymentsChart.map((record) => {
                    const color = isWeekend(record.date)
                      ? '#8884d8'
                      : '#82ca9d';
                    return <Cell key={record.time} fill={color} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {pending.companiesPayments && (
              <div className={styles.loading}>
                <div className={styles.loader} />
              </div>
            )}
          </div>
          <div className={styles.chart}>
            <ResponsiveContainer
              width={'100%'}
              height={chartHeight}
              className={cl(pending.companiesPayments && styles.opacity)}
            >
              <BarChart data={paymentsChart}>
                <Line type="monotone" dataKey="uv" stroke="#8884d8" />
                <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                {paymentsChart.length > 0 && <Legend />}
                <Bar
                  dataKey={'total'}
                  fill="#82ca9d"
                  name={t('page.reports.analytics.total')}
                >
                  {paymentsChart.map((record) => {
                    const color = isWeekend(record.date)
                      ? '#8884d8'
                      : '#82ca9d';
                    return <Cell key={record.time} fill={color} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {pending.companiesPayments && (
              <div className={styles.loading}>
                <div className={styles.loader} />
              </div>
            )}
          </div>
          <div className={styles.chart}>
            <ResponsiveContainer
              width={'100%'}
              height={chartHeight}
              className={cl(pending.payments && styles.opacity)}
            >
              <BarChart data={paymentsChart}>
                <Line type="monotone" dataKey="uv" stroke="#8884d8" />
                <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                {paymentsChart.length > 0 && <Legend />}
                <Bar
                  dataKey={'sessions'}
                  fill="#82ca9d"
                  name={t('page.reports.analytics.sessions')}
                >
                  {paymentsChart.map((record) => {
                    const color = isWeekend(record.date)
                      ? '#8884d8'
                      : '#82ca9d';
                    return <Cell key={record.time} fill={color} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {pending.companiesPayments && (
              <div className={styles.loading}>
                <div className={styles.loader} />
              </div>
            )}
          </div>
          <div className={styles.chart}>
            <ResponsiveContainer
              width={'100%'}
              height={chartHeight}
              className={cl(pending.companiesPayments && styles.opacity)}
            >
              <BarChart data={paymentsChart}>
                <Line type="monotone" dataKey="uv" stroke="#8884d8" />
                <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                {paymentsChart.length > 0 && <Legend />}
                <Bar dataKey={'system'} fill="#0088fe" name="SMS/Email" />
                <Bar
                  dataKey={'qrCode'}
                  fill="#00c49f"
                  name={t('page.reports.analytics.qrCode')}
                />
                <Bar
                  dataKey={'reception'}
                  fill="#8884d8"
                  name={t('page.reports.analytics.reception')}
                />
                <Bar
                  dataKey={'vipAccess'}
                  fill="#ff8042"
                  name={t('page.reports.analytics.vipAccess')}
                />
                <Bar
                  dataKey={'sessions'}
                  fill="#d06299"
                  name={t('page.reports.analytics.sessions')}
                />
              </BarChart>
            </ResponsiveContainer>
            {pending.companiesPayments && (
              <div className={styles.loading}>
                <div className={styles.loader} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceAnalytics;
